import {
  DraftPlacement,
  PageGeometry,
  PlacementFieldType,
  PointPx,
  RectPt,
  RectPx,
  SignatureFieldPlacement,
} from './types';

/**
 * Screen ⇄ PDF coordinate conversion for the signature-field editor
 * (AXHE-4085). Everything here is pure so the conversion — the part that is
 * invisibly wrong rather than obviously broken — can be pinned by tests.
 *
 * ## The one rule
 *
 * The persisted contract is **72 DPI PDF points, origin top-left, y downward**.
 * A US Letter page is 612 x 792 points. Nothing else may leave this module.
 *
 * Two things routinely corrupt that:
 *
 * 1. **Zoom.** The viewer draws a page at `zoom` CSS pixels per point, so a
 *    field dropped 150px from the left edge at 150% zoom is at 100pt, not
 *    150pt. Every pixel value crosses `pxToPt` before it is stored.
 * 2. **Pre-scaling to 80 DPI.** Dropbox Sign measures field *size* at 80 DPI
 *    while measuring *position* at 72. The backend does that rescale itself
 *    (`OfferLetters::FormFieldsBuilder::SIZE_SCALE`), so scaling width/height
 *    here would apply it twice — every field ~11% too big, which looks
 *    plausible in our own UI and only shows up in the signed PDF.
 *
 * Device pixel ratio deliberately plays no part: the canvas backing store is
 * oversampled for crispness, but its CSS box stays at `points * zoom`, and CSS
 * pixels are the only pixels this module ever sees.
 */

/** Smallest field we let a recruiter draw, in points (~0.11in). */
export const MIN_FIELD_PT = 8;

/**
 * Starting size per field type, in 72 DPI points. Sized against a US Letter
 * page: a signature box is 2in x 0.5in, roughly what a handwritten signature
 * occupies on a printed offer.
 */
export const DEFAULT_FIELD_SIZE_PT: Record<
  PlacementFieldType,
  { width: number; height: number }
> = {
  signature: { width: 144, height: 36 },
  initials: { width: 54, height: 36 },
  date: { width: 108, height: 24 },
  text: { width: 144, height: 24 },
  checkbox: { width: 18, height: 18 },
};

/** Rendered CSS pixels -> 72 DPI PDF points. */
export const pxToPt = (px: number, zoom: number): number => px / zoom;

/** 72 DPI PDF points -> rendered CSS pixels. */
export const ptToPx = (pt: number, zoom: number): number => pt * zoom;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), Math.max(min, max));

/**
 * Round to the integers the contract requires and keep the whole box on the
 * page. Used when a field is placed or moved: size is preserved and the
 * position gives way, which is what dragging a box against an edge should feel
 * like.
 */
export const clampRectToPage = (rect: RectPt, page: PageGeometry): RectPt => {
  const pageWidth = Math.round(page.widthPt);
  const pageHeight = Math.round(page.heightPt);
  const width = clamp(Math.round(rect.width), MIN_FIELD_PT, pageWidth);
  const height = clamp(Math.round(rect.height), MIN_FIELD_PT, pageHeight);

  return {
    x: clamp(Math.round(rect.x), 0, pageWidth - width),
    y: clamp(Math.round(rect.y), 0, pageHeight - height),
    width,
    height,
  };
};

/** A rectangle measured off the rendered page -> the stored contract. */
export const rectPxToPt = (
  rect: RectPx,
  zoom: number,
  page: PageGeometry,
): RectPt =>
  clampRectToPage(
    {
      x: pxToPt(rect.x, zoom),
      y: pxToPt(rect.y, zoom),
      width: pxToPt(rect.width, zoom),
      height: pxToPt(rect.height, zoom),
    },
    page,
  );

/**
 * The stored contract -> where to draw the box. Deliberately unrounded: this
 * is a display value, and rounding it would make a field visibly drift as the
 * recruiter changes zoom.
 */
export const rectPtToPx = (rect: RectPt, zoom: number): RectPx => ({
  x: ptToPx(rect.x, zoom),
  y: ptToPx(rect.y, zoom),
  width: ptToPx(rect.width, zoom),
  height: ptToPx(rect.height, zoom),
});

export interface PlaceFieldInput {
  /** Where the recruiter clicked or dropped, in CSS px from the page corner. */
  pointPx: PointPx;
  zoom: number;
  page: PageGeometry;
  /** 1-based page number. */
  pageNumber: number;
  type: PlacementFieldType;
  signer: number;
}

/**
 * Build a placement centred on the drop point. Centring (rather than using the
 * point as the top-left corner) matches the drag ghost the recruiter sees, so
 * the field lands where they aimed it.
 */
export const placeField = ({
  pointPx,
  zoom,
  page,
  pageNumber,
  type,
  signer,
}: PlaceFieldInput): SignatureFieldPlacement => {
  const size = DEFAULT_FIELD_SIZE_PT[type];
  const centre = {
    x: pxToPt(pointPx.x, zoom),
    y: pxToPt(pointPx.y, zoom),
  };

  return {
    type,
    signer,
    page: pageNumber,
    required: true,
    ...clampRectToPage(
      {
        x: centre.x - size.width / 2,
        y: centre.y - size.height / 2,
        width: size.width,
        height: size.height,
      },
      page,
    ),
  };
};

/** Move a placed field by a pointer delta measured in rendered pixels. */
export const movedByPx = (
  rect: RectPt,
  deltaPx: PointPx,
  zoom: number,
  page: PageGeometry,
): RectPt =>
  clampRectToPage(
    {
      ...rect,
      x: rect.x + pxToPt(deltaPx.x, zoom),
      y: rect.y + pxToPt(deltaPx.y, zoom),
    },
    page,
  );

/**
 * Resize by a pointer delta, anchored at the field's top-left corner.
 *
 * This does NOT go through `clampRectToPage`: that clamp keeps the size and
 * gives up the position, which is right for a move and wrong here — dragging
 * the bottom-right handle past the page edge would slide the whole box left
 * instead of stopping. The size gives way instead, so the anchored corner
 * stays put.
 */
export const resizedByPx = (
  rect: RectPt,
  deltaPx: PointPx,
  zoom: number,
  page: PageGeometry,
): RectPt => ({
  ...rect,
  width: clamp(
    Math.round(rect.width + pxToPt(deltaPx.x, zoom)),
    MIN_FIELD_PT,
    Math.round(page.widthPt) - rect.x,
  ),
  height: clamp(
    Math.round(rect.height + pxToPt(deltaPx.y, zoom)),
    MIN_FIELD_PT,
    Math.round(page.heightPt) - rect.y,
  ),
});

/**
 * Mirrors `FormFieldsBuilder#validate_geometry!`. A placement that fails this
 * would be rejected on send — after the recruiter has left the editor — so we
 * catch it at the save boundary instead.
 */
export const isEmittable = (placement: SignatureFieldPlacement): boolean =>
  Number.isInteger(placement.x) &&
  placement.x >= 0 &&
  Number.isInteger(placement.y) &&
  placement.y >= 0 &&
  Number.isInteger(placement.width) &&
  placement.width > 0 &&
  Number.isInteger(placement.height) &&
  placement.height > 0 &&
  Number.isInteger(placement.page) &&
  placement.page >= 1 &&
  Number.isInteger(placement.signer) &&
  placement.signer >= 0;

/**
 * Drop the client-only `id` and emit in reading order (page, then down, then
 * across). The order is cosmetic to Dropbox Sign but makes the stored jsonb —
 * and any diff of it during an incident — legible.
 */
export const toPayload = (
  placements: DraftPlacement[],
): SignatureFieldPlacement[] =>
  [...placements]
    .sort(
      (a, b) => a.page - b.page || a.y - b.y || a.x - b.x || a.signer - b.signer,
    )
    .map(({ id: _id, ...placement }) => placement);
